'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';

import { TimeGridSlots } from './schedule-grid.component';

import { Button } from '@/components/ui/button/button.component';
import { Carousel, CarouselContent } from '@/components/ui/carousel.component';
import { formatVisibleGrid, groupByDateToTimeGrid } from '@/app/(main)/(pages)/busca/[slug]/_consts';
import { Icon } from '@/components/ui/icon.component';
import { SchedulesSearchSlot, ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { useGridCol } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-grid-col.hook';
import { useGridRows } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-grid-rows.hook';

interface ScheduleTimeProps {
  slots?: SchedulesSearchSlot[];
  scheduleType: ScheduleType;
  fetchNewSlots: () => void;
  hasEnded?: boolean;
}

export function ScheduleTimeGrid({ slots, scheduleType, fetchNewSlots, hasEnded }: ScheduleTimeProps) {
  const cols = useGridCol({ scheduleType });
  const rows = useGridRows();

  const [showAll, setShowAll] = useState(false);

  const groupedGrid = useMemo(() => groupByDateToTimeGrid(slots), [slots]);

  const isShowAllVisible = useMemo(() => groupedGrid.some((day) => day.times.length > rows), [groupedGrid, rows]);

  const grid = useMemo(() => formatVisibleGrid(groupedGrid, rows, showAll), [groupedGrid, rows, showAll]);

  const scrollableRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (showAll && scrollableRef.current) {
      scrollableRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showAll]);

  return (
    <div className="flex flex-col items-center gap-4">
      <Carousel
        opts={{
          loop: false,
          skipSnaps: true,
          align: 'start',
          slidesToScroll: cols,
        }}
        arrowsPosition="top"
        showArrows
        className="w-[260px] sm:w-[calc(100%-85px)]"
        noArrowsGap
      >
        <CarouselContent endRef={scrollableRef} className="flex pl-1 -ml-3" overflowClassName="px-1">
          <TimeGridSlots
            scheduleType={scheduleType}
            timeGrid={grid}
            fetchNewSlots={fetchNewSlots}
            showAll={showAll}
            hasEnded={hasEnded}
          />
        </CarouselContent>
      </Carousel>
      {isShowAllVisible && (
        <Button
          variant="text"
          className="w-full gap-2 flex pb-3"
          onClick={() => setShowAll((state) => !state)}
          data-testid="schedule-modal--see-more"
        >
          <Icon icon={showAll ? faMinus : faPlus} className="text-green-500" />
          {showAll ? 'Ver menos horários' : 'Ver mais horários'}
        </Button>
      )}
    </div>
  );
}
